import { useState } from 'react'

/**
 * Absolute http(s) only. A broker's `logo_url` is a RELATIVE media path on the wire
 * (`broker/logo/….png`), which `brokerService` makes absolute on the logo CDN. A relative path
 * that still reached a mark would resolve against the app's own origin and 404, so it is treated
 * as no logo at all, as a load error is.
 */
const isUsableLogoUrl = (url: string | null | undefined): url is string =>
  url !== null && url !== undefined && /^https?:\/\//i.test(url)

interface BrokerLogo {
  /** The logo to draw, or null for the numbered badge. */
  src: string | null
  /** For the `<img>`: a failed load falls back to the badge. */
  onError: () => void
}

/**
 * Which face a broker mark shows: its logo when the wire has a usable one and it loaded, its
 * numbered badge otherwise. Moved here from the By-broker header (2026-10-28) so every mark and
 * every name beside one (`BrokerIdentity`'s trailing code) reads the same answer.
 *
 * The failed flag is derived state keyed on `code|logoUrl`: a picker changes the broker WITHOUT
 * unmounting the mark, and a plain `useState(false)` would carry broker A's failed image forward
 * and hide broker B's good one.
 */
export function useBrokerLogo(code: string, logoUrl: string | null | undefined): BrokerLogo {
  const key = `${code}|${logoUrl ?? ''}`
  const [state, setState] = useState({ key, failed: false })
  if (state.key !== key) setState({ key, failed: false })

  const failed = state.key === key && state.failed
  return {
    src: !failed && isUsableLogoUrl(logoUrl) ? logoUrl : null,
    onError: () => {
      // A slow image's error can land after the mark has moved on to another broker.
      setState((previous) => (previous.key === key ? { key, failed: true } : previous))
    },
  }
}
